import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { CirclePlus, Search, X } from "lucide-react";
import ChatList from "../../components/ChatList";
import ChatWindow from "../../components/ChatWindow";
import { getDisplayAvatar, getDisplayName, showsAsStore } from "../../components/ChatItem";
import StoryBar from "../story/StoryBar";
import defaultAvatar from "../../assets/profile.png";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Leftsidebar, { refreshChatUnreadCount } from "../sidebar/leftsidebar";
import { useSocket } from "../../contexts/SocketContext";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const PAGE_SIZE = 20;
// First page of the list, kept for this tab (sessionStorage is cleared on login/logout) so the page
// renders at once and refreshes in the background
const CHAT_LIST_CACHE_KEY = "chatListCache";

const readChatListCache = () => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CHAT_LIST_CACHE_KEY) || "null");
    return Array.isArray(cached?.chats) ? cached : null;
  } catch {
    return null;
  }
};

const toChat = (row) => ({
  id: row.id,
  name: getDisplayName(row),
  username: row.username,
  avatar: getDisplayAvatar(row),
  sellerId: row.seller?.id || null,
  asStore: showsAsStore(row),
  lastMessage: row.last_message,
  lastMessageMedia: row.last_message_media,
  lastMessageAt: row.last_message_created_at,
  lastSenderId: row.last_message_sender_id,
  unread: Number(row.unread_count) || 0,
  myStatus: row.myStatus || null,
  friendStatus: row.friendStatus || null,
});

// A person with no messages yet (opened from friend search or a ?user= link)
const userToChat = (user) => ({
  id: user.id,
  name: getDisplayName(user),
  username: user.username,
  avatar: getDisplayAvatar(user),
  sellerId: user.seller?.id || null,
  asStore: showsAsStore(user),
  lastMessage: "",
  unread: 0,
  isNew: true,
  myStatus: null,
  friendStatus: null,
});

// "Create Chat": pick one of your friends to start (or reopen) a conversation
const NewChatModal = ({ token, currentUserId, onSelect, onClose }) => {
  const [query, setQuery] = useState("");
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const term = query.trim();
        const { data } = await axios.post(
          `${API_BASE_URL}/user/get-all-users`,
          { filters: { isFriendsOnly: true, ...(term ? { search: term } : {}) }, page: 1, limit: 20 },
          { headers: { token } }
        );
        if (!cancelled) {
          setFriends((data?.result?.users || []).filter((u) => String(u.id) !== String(currentUserId)));
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || "Failed to load your friends.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query ? 400 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, token, currentUserId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Start a new chat"
        className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#000000]">Start a new chat</h3>
          <button onClick={onClose} aria-label="Close" className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="relative mb-4">
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your friends"
            className="w-full pl-10 pr-4 py-3 text-sm rounded-xl bg-[#F0F0F0] outline-none focus:ring-2 focus:ring-green-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]" />
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading && <p className="text-sm text-gray-500 text-center py-4">Loading...</p>}
          {!loading && error && <p className="text-sm text-red-500 text-center py-4">{error}</p>}
          {!loading && !error && friends.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              {query.trim() ? "No friends match your search." : "You have no friends to chat with yet."}
            </p>
          )}
          {!loading &&
            friends.map((friend) => (
              <button
                type="button"
                key={friend.id}
                onClick={() => onSelect(friend)}
                className="w-full flex items-center gap-3 p-2 rounded-lg text-left hover:bg-gray-100"
              >
                <img
                  src={friend.profile_picture_url || defaultAvatar}
                  alt={getDisplayName(friend)}
                  className="w-11 h-11 rounded-lg border object-cover"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#000000] truncate">{getDisplayName(friend)}</p>
                  {friend.username && <p className="text-xs text-[#797C7B] truncate">@{friend.username}</p>}
                </div>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

const ChatsPage = () => {
  const { socket, token, currentUser, isConnected } = useSocket();
  const currentUserId = currentUser?.id;
  const [searchParams] = useSearchParams();
  const [cachedList] = useState(readChatListCache);
  const [chats, setChats] = useState(() => cachedList?.chats || []);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(!cachedList);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(() => !!cachedList && cachedList.chats.length < cachedList.total);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [friendResults, setFriendResults] = useState([]);
  const [searchingFriends, setSearchingFriends] = useState(false);
  const [onlineIds, setOnlineIds] = useState(() => new Set());
  const [totalChats, setTotalChats] = useState(() => cachedList?.total || 0);
  const hasListRef = useRef(!!cachedList);
  const [showNewChat, setShowNewChat] = useState(false);
  const selectedChatRef = useRef(null);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  const loadChats = useCallback(
    async (pageToLoad) => {
      if (!token) return;
      if (pageToLoad > 1) setLoadingMore(true);
      else if (!hasListRef.current) setLoading(true);
      setError("");
      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/group_chat/get-chat-friends`,
          { page: pageToLoad, limit: PAGE_SIZE },
          { headers: { token } }
        );
        const rows = (data?.result?.friends || []).map(toChat);
        if (pageToLoad === 1) hasListRef.current = true;
        setChats((prev) => {
          if (pageToLoad > 1) {
            const ids = new Set(prev.map((c) => String(c.id)));
            return [...prev, ...rows.filter((c) => !ids.has(String(c.id)))];
          }
          const ids = new Set(rows.map((c) => String(c.id)));
          return [...prev.filter((c) => c.isNew && !ids.has(String(c.id))), ...rows];
        });
        setPage(pageToLoad);
        setTotalChats(Number(data?.result?.total) || 0);
        setHasMore(pageToLoad * PAGE_SIZE < (Number(data?.result?.total) || 0));
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load chats.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [token]
  );

  useEffect(() => {
    loadChats(1);
  }, [loadChats]);

  // Mirror the live list (new messages, read, cleared chats) into the cache
  useEffect(() => {
    if (loading || !hasListRef.current) return;
    try {
      sessionStorage.setItem(
        CHAT_LIST_CACHE_KEY,
        JSON.stringify({ chats: chats.filter((c) => !c.isNew).slice(0, PAGE_SIZE), total: totalChats })
      );
    } catch {}
  }, [chats, totalChats, loading]);

  const openChat = useCallback((chat) => {
    setSelectedChat(chat);
    setChats((prev) =>
      prev.some((c) => String(c.id) === String(chat.id))
        ? prev.map((c) => (String(c.id) === String(chat.id) ? { ...c, unread: 0 } : c))
        : [chat, ...prev]
    );
  }, []);

  const startChatWith = useCallback(
    (user) => {
      setSearch("");
      setFriendResults([]);
      setShowNewChat(false);
      const existing = chats.find((c) => String(c.id) === String(user.id));
      openChat(existing || userToChat(user));
    },
    [chats, openChat]
  );

  // /chats?user=<id or username> opens that conversation directly
  const userParam = searchParams.get("user");
  const openedFromLinkRef = useRef(null);
  useEffect(() => {
    if (!userParam || !token || !currentUserId || loading || openedFromLinkRef.current === userParam) return;
    openedFromLinkRef.current = userParam;
    const existing = chats.find((c) => String(c.id) === userParam || c.username === userParam);
    if (existing) {
      openChat(existing);
      return;
    }
    axios
      .get(`${API_BASE_URL}/user/profile`, { params: { id: userParam }, headers: { token } })
      .then(({ data }) => {
        const user = data?.result;
        if (user?.id && String(user.id) !== String(currentUserId)) openChat(userToChat(user));
      })
      .catch(() => {});
  }, [userParam, token, currentUserId, loading, chats, openChat]);

  // Friend search for starting a new conversation
  useEffect(() => {
    const term = search.trim();
    if (!term || !token) {
      setFriendResults([]);
      setSearchingFriends(false);
      return;
    }
    setSearchingFriends(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/user/get-all-users`,
          { filters: { isFriendsOnly: true, search: term }, page: 1, limit: 10 },
          { headers: { token } }
        );
        if (!cancelled) setFriendResults(data?.result?.users || []);
      } catch {
        if (!cancelled) setFriendResults([]);
      } finally {
        if (!cancelled) setSearchingFriends(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, token]);

  const chatIds = useMemo(() => new Set(chats.map((c) => String(c.id))), [chats]);
  const newFriendResults = useMemo(
    () => friendResults.filter((f) => !chatIds.has(String(f.id)) && String(f.id) !== String(currentUserId)),
    [friendResults, chatIds, currentUserId]
  );

  // Keep the list live: new messages move a conversation to the top and update its unread count
  useEffect(() => {
    if (!socket || !currentUserId) return;
    const me = Number(currentUserId);

    const onReceive = (msg) => {
      if (!msg || msg.is_group_chat) return;
      const mine = Number(msg.sender_id) === me;
      if (!mine && Number(msg.reciever_id) !== me) return;
      const otherId = String(mine ? msg.reciever_id : msg.sender_id);
      const isOpen = String(selectedChatRef.current?.id) === otherId;
      setChats((prev) => {
        const existing = prev.find((c) => String(c.id) === otherId);
        const base = existing || (!mine && msg.sender ? userToChat(msg.sender) : null);
        if (!base) return prev;
        const updated = {
          ...base,
          isNew: false,
          lastMessage: msg.message,
          lastMessageMedia: msg.media_url,
          lastMessageAt: msg.created_at,
          lastSenderId: msg.sender_id,
          unread: !mine && !isOpen ? (base.unread || 0) + 1 : base.unread || 0,
        };
        return [updated, ...prev.filter((c) => String(c.id) !== otherId)];
      });
    };

    const onRead = ({ reader_id, sender_id } = {}) => {
      if (Number(reader_id) !== me) return;
      setChats((prev) => prev.map((c) => (String(c.id) === String(sender_id) ? { ...c, unread: 0 } : c)));
    };

    const onStatus = ({ userId, online } = {}) => {
      if (userId === undefined || userId === null) return;
      setOnlineIds((prev) => {
        const next = new Set(prev);
        if (online) next.add(String(userId));
        else next.delete(String(userId));
        return next;
      });
    };

    // Another user blocked/unblocked me
    const onBlock = (payload) => {
      const actor = payload?.sender;
      if (!actor || Number(actor.id) === me) return;
      const changes = { friendStatus: actor.myStatus || null };
      setChats((prev) => prev.map((c) => (String(c.id) === String(actor.id) ? { ...c, ...changes } : c)));
      setSelectedChat((prev) => (prev && String(prev.id) === String(actor.id) ? { ...prev, ...changes } : prev));
    };

    socket.on("receive-message", onReceive);
    socket.on("messages-read", onRead);
    socket.on("user-status", onStatus);
    socket.on("block-user", onBlock);
    return () => {
      socket.off("receive-message", onReceive);
      socket.off("messages-read", onRead);
      socket.off("user-status", onStatus);
      socket.off("block-user", onBlock);
    };
  }, [socket, currentUserId]);

  // Initial online status for the people in the list
  const chatIdsKey = chats.map((c) => c.id).join(",");
  useEffect(() => {
    if (!socket || !isConnected || !chatIdsKey) return;
    socket.emit("get-online-status", chatIdsKey.split(","), (res) => {
      if (res?.success) setOnlineIds(new Set((res.online || []).map(String)));
    });
  }, [socket, isConnected, chatIdsKey]);

  // Keep the sidebar badge in step when messages arrive or chats are read (the sidebar fetched it on load).
  // Debounced so the server-side mark-read has finished before the count is read again.
  const unreadKey = chats.map((c) => `${c.id}:${c.unread || 0}`).sort().join(",");
  const lastUnreadKeyRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    if (lastUnreadKeyRef.current === null) {
      lastUnreadKeyRef.current = unreadKey;
      return;
    }
    if (lastUnreadKeyRef.current === unreadKey) return;
    lastUnreadKeyRef.current = unreadKey;
    const timer = setTimeout(refreshChatUnreadCount, 1500);
    return () => clearTimeout(timer);
  }, [unreadKey, loading]);

  const handleChatUpdate = useCallback((chatId, changes) => {
    setChats((prev) => prev.map((c) => (String(c.id) === String(chatId) ? { ...c, ...changes } : c)));
    setSelectedChat((prev) => (prev && String(prev.id) === String(chatId) ? { ...prev, ...changes } : prev));
  }, []);

  const handleChatCleared = useCallback((chatId) => {
    setChats((prev) => prev.filter((c) => String(c.id) !== String(chatId)));
    setSelectedChat(null);
  }, []);

  const closeNewChat = useCallback(() => setShowNewChat(false), []);
  const chatCount = Math.max(totalChats, chats.filter((c) => !c.isNew).length);

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <StoryBar />

        {/* Header */}
        <div className="flex items-center justify-between gap-3 mt-4 mb-4">
          <div className="flex items-center gap-3 border-b-2 border-[#095228] pb-2 pr-4">
            <h3 className="text-2xl text-[#000000] font-semibold">Chats</h3>
            <span className="bg-[#095228] text-white text-xs min-w-[28px] h-7 px-2 rounded-full font-bold flex items-center justify-center">
              {chatCount}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowNewChat(true)}
            className="flex items-center gap-2 bg-[#D9F5F7] border border-[#5BC8D1] text-[#21A1AE] text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#c9eff2]"
          >
            <CirclePlus className="w-5 h-5" />
            Create Chat
          </button>
        </div>

        <div className="flex gap-4 min-w-0">
          {/* On mobile show one pane at a time: the list, or the open chat */}
          <div className={`${selectedChat ? "hidden md:block" : "w-full"} md:w-[280px] lg:w-[300px] shrink-0`}>
            <ChatList
              chats={chats}
              search={search}
              onSearchChange={setSearch}
              friendResults={newFriendResults}
              searchingFriends={searchingFriends}
              selectedChatId={selectedChat?.id}
              onlineIds={onlineIds}
              currentUserId={currentUserId}
              loading={loading}
              error={error}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={() => loadChats(page + 1)}
              onRetry={() => loadChats(1)}
              onSelectChat={openChat}
              onStartChat={startChatWith}
              onCreateChat={() => setShowNewChat(true)}
            />
          </div>
          {selectedChat ? (
            <div className="flex flex-1 min-w-0 flex-col">
              <button
                onClick={() => setSelectedChat(null)}
                className="md:hidden self-start mb-2 text-sm font-medium text-[#095228]"
              >
                &larr; Chats
              </button>
              <ChatWindow
                key={selectedChat.id}
                chat={selectedChat}
                isOnline={onlineIds.has(String(selectedChat.id))}
                onChatUpdate={handleChatUpdate}
                onChatCleared={handleChatCleared}
              />
            </div>
          ) : (
            <div className="hidden md:flex flex-1 min-h-[420px] items-center justify-center rounded-2xl border border-[#00000033] text-gray-400">
              Select a chat to start messaging
            </div>
          )}
        </div>

        {showNewChat && (
          <NewChatModal
            token={token}
            currentUserId={currentUserId}
            onSelect={startChatWith}
            onClose={closeNewChat}
          />
        )}
      </main>
    </div>
  );
};

export default ChatsPage;
